import type {
  DictaphoneDeviceFingerprintInput,
  DictaphoneDeviceMapping,
  DictaphoneKeyMappingInput,
  GamepadActionId,
} from 'generated/graphql';
import { cleanNullableInputLabel } from 'domains/reporter/useMicrophone/utils';
import { DICTAPHONE_BUTTON_ACTION_LABELS } from 'utils/dictaphone/actions';
import type { DictaphoneButton, DictaphoneKeyMapping } from 'utils/dictaphone/products/types';

export type AssignedAction = GamepadActionId | null;

export const isGamepadActionId = (value: unknown): value is GamepadActionId =>
  typeof value === 'string' &&
  Object.prototype.hasOwnProperty.call(DICTAPHONE_BUTTON_ACTION_LABELS, value);

export const createAssignmentsFromKeyMapping = (
  buttons: ReadonlyArray<DictaphoneButton>,
  keyMapping?: DictaphoneKeyMapping
): Record<string, AssignedAction> =>
  Object.fromEntries(
    buttons.map(({ name, keyCode }) => [name, keyMapping?.[String(keyCode)] ?? null])
  ) as Record<string, AssignedAction>;

export const toKeyMappingInput = (
  buttons: ReadonlyArray<DictaphoneButton>,
  assignments: Record<string, AssignedAction>
): ReadonlyArray<DictaphoneKeyMappingInput> =>
  buttons
    .map(({ name, keyCode }) => {
      const actionId = assignments[name];
      if (actionId == null) {
        return null;
      }
      return {
        key: String(keyCode),
        actionId,
      };
    })
    .filter((entry): entry is DictaphoneKeyMappingInput => entry != null);

export const updateAssignmentsForAction = (
  assignments: Record<string, AssignedAction>,
  buttonName: string,
  actionId: AssignedAction
): Record<string, AssignedAction> => {
  // Ensure an action is mapped to only one button at a time.
  const nextAssignments = { ...assignments };
  if (actionId != null) {
    for (const [name, assignedAction] of Object.entries(assignments)) {
      if (name !== buttonName && assignedAction === actionId) {
        nextAssignments[name] = null;
      }
    }
  }
  nextAssignments[buttonName] = actionId;
  return nextAssignments;
};

const toFingerprintFromDeviceMapping = (
  deviceMapping: DictaphoneDeviceMapping | undefined
): DictaphoneDeviceFingerprintInput | null => {
  const fingerprint = deviceMapping?.deviceFingerprint;
  return fingerprint == null
    ? null
    : {
        deviceLabel: cleanNullableInputLabel(fingerprint.deviceLabel),
        productId: fingerprint.productId ?? undefined,
        productName: fingerprint.productName ?? undefined,
        vendorId: fingerprint.vendorId ?? undefined,
      };
};

const toFingerprintFromDeviceLabel = (
  audioInputDeviceLabel: string | undefined
): DictaphoneDeviceFingerprintInput | null => {
  const deviceLabel = cleanNullableInputLabel(audioInputDeviceLabel);
  return deviceLabel == null ? null : { deviceLabel };
};

// Prefer a previously saved device fingerprint; otherwise fall back to the active audio label.
// Return null when neither source yields a usable label so submits can be safely skipped.
export const resolveDeviceFingerprintForUpdate = (
  deviceMapping: DictaphoneDeviceMapping | undefined,
  audioInputDeviceLabel: string | undefined
): DictaphoneDeviceFingerprintInput | null => {
  return (
    toFingerprintFromDeviceMapping(deviceMapping) ??
    toFingerprintFromDeviceLabel(audioInputDeviceLabel)
  );
};
